import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { AppLayout } from '@/components/layout';
import { PostItNote, TextElement, LineElement, BoxElement, EllipseElement, DiamondElement, ConnectorElement, ImageElement } from '@/components/canvas';
import { Toolbar } from '@/components/canvas/Toolbar';
import { ShareDialog, CreatePostItWizard, ImageLibraryDialog } from '@/components/dialogs';
import { useThinkSpace, useImportExport } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import type {
  CanvasElement,
  PostItColor,
  Position,
  ConnectionPointPosition,
} from '@/types/thinkspace';
import { findNearestConnectionPoint } from '@/utils/connectorUtils';

type ToolType = 'select' | 'postit' | 'text' | 'line' | 'box' | 'ellipse' | 'diamond' | 'connector';

export const ThinkSpacePage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const {
    thinkSpace,
    loading,
    error,
    access,
    addPostIt,
    addText,
    addLine,
    addBox,
    addEllipse,
    addDiamond,
    addConnector,
    addImage,
    updateElement,
    deleteElement,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
  } = useThinkSpace(id || '');

  const { exportAsThinkSpace, exportAsPNG, exportAsSVG, importThinkSpace } = useImportExport();

  // Ref for the canvas content (for PNG export)
  const canvasContentRef = useRef<HTMLDivElement>(null);

  // UI State
  const [activeTool, setActiveTool] = useState<ToolType>('select');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  // Default values for new elements (can be changed via properties dialog after creation)
  const defaultPostItColor: PostItColor = 'yellow';
  const defaultStrokeColor = '#151414';
  const defaultStrokeWidth = 2;
  const defaultFontSize = 16;
  const [pan] = useState({ x: 0, y: 0 });
  const [shareOpen, setShareOpen] = useState(searchParams.get('share') === 'true');
  const [wizardOpen, setWizardOpen] = useState(searchParams.get('wizard') === 'true');
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<Position | null>(null);
  const [tempElement, setTempElement] = useState<CanvasElement | null>(null);

  // Connector snapping state
  const [connectorStartSnap, setConnectorStartSnap] = useState<{
    elementId: string;
    position: ConnectionPointPosition;
    point: Position;
  } | null>(null);
  const [connectorEndSnap, setConnectorEndSnap] = useState<{
    elementId: string;
    position: ConnectionPointPosition;
    point: Position;
  } | null>(null);

  // Drag state
  const [dragging, setDragging] = useState<{ id: string; startPos: Position; elementStart: Position } | null>(null);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<string | null>(null);

  // Refs
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get canvas position from mouse/touch event
  const getCanvasPosition = useCallback(
    (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent): Position => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0]?.clientX || 0 : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0]?.clientY || 0 : (e as MouseEvent).clientY;

      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    },
    [pan, zoom]
  );

  // Handle canvas click for adding elements
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (!access.canEdit) return;

      const pos = getCanvasPosition(e);

      switch (activeTool) {
        case 'postit':
          addPostIt({
            title: 'New Note',
            content: '',
            color: defaultPostItColor,
            position: pos,
          });
          setActiveTool('select');
          break;
        case 'text':
          addText({
            content: 'New Text',
            fontSize: defaultFontSize,
            color: defaultStrokeColor,
            position: pos,
          });
          setActiveTool('select');
          break;
        case 'select':
          setSelectedId(null);
          break;
      }
    },
    [access.canEdit, activeTool, getCanvasPosition, addPostIt, addText]
  );

  // Handle drawing start (for lines, boxes, ellipses, diamonds, and connectors)
  const handleDrawStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!access.canEdit || (activeTool !== 'line' && activeTool !== 'box' && activeTool !== 'ellipse' && activeTool !== 'diamond' && activeTool !== 'connector')) return;

      e.preventDefault();
      const pos = getCanvasPosition(e);

      // For connectors, check if we're starting near a connection point
      if (activeTool === 'connector' && thinkSpace) {
        const SNAP_DISTANCE = 25;
        for (const element of thinkSpace.elements) {
          if (element.type === 'connector') continue; // Skip connectors
          const nearest = findNearestConnectionPoint(pos, element, SNAP_DISTANCE);
          if (nearest) {
            setConnectorStartSnap({
              elementId: element.id,
              position: nearest.position,
              point: nearest.point,
            });
            setIsDrawing(true);
            setDrawStart(nearest.point); // Snap to the connection point
            return;
          }
        }
        // No snap found, clear any previous snap
        setConnectorStartSnap(null);
      }

      setIsDrawing(true);
      setDrawStart(pos);
    },
    [access.canEdit, activeTool, getCanvasPosition, thinkSpace]
  );

  // Handle drawing move
  const handleDrawMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || !drawStart) return;

      const pos = getCanvasPosition(e);

      if (activeTool === 'line') {
        setTempElement({
          id: 'temp',
          type: 'line',
          position: drawStart,
          endPosition: pos,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
          zIndex: 9999,
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: '',
        });
      } else if (activeTool === 'box') {
        const width = Math.abs(pos.x - drawStart.x);
        const height = Math.abs(pos.y - drawStart.y);
        setTempElement({
          id: 'temp',
          type: 'box',
          position: {
            x: Math.min(drawStart.x, pos.x),
            y: Math.min(drawStart.y, pos.y),
          },
          size: { width, height },
          fillColor: null,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
          borderRadius: 0,
          zIndex: 9999,
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: '',
        });
      } else if (activeTool === 'ellipse') {
        const width = Math.abs(pos.x - drawStart.x);
        const height = Math.abs(pos.y - drawStart.y);
        setTempElement({
          id: 'temp',
          type: 'ellipse',
          position: {
            x: Math.min(drawStart.x, pos.x),
            y: Math.min(drawStart.y, pos.y),
          },
          size: { width, height },
          fillColor: null,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
          zIndex: 9999,
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: '',
        });
      } else if (activeTool === 'diamond') {
        const width = Math.abs(pos.x - drawStart.x);
        const height = Math.abs(pos.y - drawStart.y);
        setTempElement({
          id: 'temp',
          type: 'diamond',
          position: {
            x: Math.min(drawStart.x, pos.x),
            y: Math.min(drawStart.y, pos.y),
          },
          size: { width, height },
          fillColor: null,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
          zIndex: 9999,
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: '',
        });
      } else if (activeTool === 'connector') {
        // Check for snap at end point
        let endPos = pos;
        let endSnap: typeof connectorEndSnap = null;

        if (thinkSpace) {
          const SNAP_DISTANCE = 25;
          for (const element of thinkSpace.elements) {
            if (element.type === 'connector') continue;
            const nearest = findNearestConnectionPoint(pos, element, SNAP_DISTANCE);
            if (nearest) {
              endSnap = {
                elementId: element.id,
                position: nearest.position,
                point: nearest.point,
              };
              endPos = nearest.point;
              break;
            }
          }
        }
        setConnectorEndSnap(endSnap);

        setTempElement({
          id: 'temp',
          type: 'connector',
          position: drawStart,
          startConnection: connectorStartSnap ? {
            elementId: connectorStartSnap.elementId,
            position: connectorStartSnap.position,
          } : null,
          endConnection: endSnap ? {
            elementId: endSnap.elementId,
            position: endSnap.position,
          } : null,
          startPosition: drawStart,
          endPosition: endPos,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
          routingPoints: [], // Will be calculated on final placement
          zIndex: 9999,
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: '',
        });
      }
    },
    [isDrawing, drawStart, activeTool, getCanvasPosition, thinkSpace, connectorStartSnap, connectorEndSnap]
  );

  // Handle drawing end
  const handleDrawEnd = useCallback(() => {
    if (!isDrawing || !drawStart || !tempElement) {
      setIsDrawing(false);
      setDrawStart(null);
      setTempElement(null);
      return;
    }

    if (tempElement.type === 'line') {
      addLine({
        startPosition: drawStart,
        endPosition: tempElement.endPosition,
        strokeColor: defaultStrokeColor,
        strokeWidth: defaultStrokeWidth,
      });
    } else if (tempElement.type === 'box' && tempElement.size.width > 10 && tempElement.size.height > 10) {
      addBox({
        position: tempElement.position,
        size: tempElement.size,
        strokeColor: defaultStrokeColor,
        strokeWidth: defaultStrokeWidth,
      });
    } else if (tempElement.type === 'ellipse' && tempElement.size.width > 10 && tempElement.size.height > 10) {
      addEllipse({
        position: tempElement.position,
        size: tempElement.size,
        strokeColor: defaultStrokeColor,
        strokeWidth: defaultStrokeWidth,
      });
    } else if (tempElement.type === 'diamond' && tempElement.size.width > 10 && tempElement.size.height > 10) {
      addDiamond({
        position: tempElement.position,
        size: tempElement.size,
        strokeColor: defaultStrokeColor,
        strokeWidth: defaultStrokeWidth,
      });
    } else if (tempElement.type === 'connector') {
      // Only add if there's meaningful distance
      const dx = tempElement.endPosition.x - tempElement.startPosition.x;
      const dy = tempElement.endPosition.y - tempElement.startPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 20) {
        addConnector({
          startPosition: tempElement.startPosition,
          endPosition: tempElement.endPosition,
          startConnection: tempElement.startConnection,
          endConnection: tempElement.endConnection,
          strokeColor: defaultStrokeColor,
          strokeWidth: defaultStrokeWidth,
        });
      }
    }

    setIsDrawing(false);
    setDrawStart(null);
    setTempElement(null);
    setConnectorStartSnap(null);
    setConnectorEndSnap(null);
    setActiveTool('select');
  }, [isDrawing, drawStart, tempElement, addLine, addBox, addEllipse, addDiamond, addConnector]);

  // Handle element drag
  const handleDragStart = useCallback(
    (elementId: string, e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const element = thinkSpace?.elements.find((el) => el.id === elementId);
      if (!element) return;

      const pos = getCanvasPosition(e);
      setDragging({
        id: elementId,
        startPos: pos,
        elementStart: { ...element.position },
      });
      setSelectedId(elementId);
    },
    [thinkSpace?.elements, getCanvasPosition]
  );

  // Handle drag move
  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const pos = getCanvasPosition(e);
      const dx = pos.x - dragging.startPos.x;
      const dy = pos.y - dragging.startPos.y;

      updateElement(dragging.id, {
        position: {
          x: dragging.elementStart.x + dx,
          y: dragging.elementStart.y + dy,
        },
      });
    };

    const handleUp = () => {
      setDragging(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [dragging, getCanvasPosition, updateElement]);

  // Handle Delete key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Delete or Backspace (on Mac)
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;

      // Don't trigger if no element is selected
      if (!selectedId) return;

      // Don't trigger if user is editing text (input, textarea, or contentEditable)
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      // Prevent default browser behavior (like navigating back on Backspace)
      e.preventDefault();

      // Set element to delete and open confirmation dialog
      setElementToDelete(selectedId);
      setDeleteConfirmOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);

  // Handle confirmed delete
  const handleConfirmDelete = useCallback(() => {
    if (elementToDelete) {
      deleteElement(elementToDelete);
      setSelectedId(null);
    }
    setDeleteConfirmOpen(false);
    setElementToDelete(null);
  }, [elementToDelete, deleteElement]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setElementToDelete(null);
  }, []);

  // Handle export
  const handleExport = useCallback(async (format: 'tspace' | 'png' | 'svg') => {
    if (!thinkSpace) return;

    try {
      switch (format) {
        case 'tspace':
          exportAsThinkSpace(thinkSpace);
          setSnackbar({ open: true, message: 'Exported as ThinkSpace format!', severity: 'success' });
          break;
        case 'png':
          if (canvasContentRef.current) {
            await exportAsPNG(thinkSpace, canvasContentRef.current);
            setSnackbar({ open: true, message: 'Exported as PNG!', severity: 'success' });
          }
          break;
        case 'svg':
          exportAsSVG(thinkSpace);
          setSnackbar({ open: true, message: 'Exported as SVG!', severity: 'success' });
          break;
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
    }
  }, [thinkSpace, exportAsThinkSpace, exportAsPNG, exportAsSVG]);

  // Handle import
  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !id || !user) return;

      try {
        await importThinkSpace(file, id, user.uid, 'merge');
        setSnackbar({ open: true, message: 'Imported successfully!', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to import file', severity: 'error' });
      }

      e.target.value = '';
    },
    [id, user, importThinkSpace]
  );

  // Handle wizard create - place post-it in center of visible viewport
  const handleWizardCreate = useCallback(
    (input: { title: string; content: string; color: PostItColor }) => {
      // Calculate center of visible viewport
      const canvas = canvasRef.current;
      let centerX = 400; // fallback
      let centerY = 300; // fallback

      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        // Get the center of the viewport in canvas coordinates
        centerX = (-pan.x + rect.width / 2) / zoom - 100; // -100 to offset for post-it size
        centerY = (-pan.y + rect.height / 2) / zoom - 100;
      }

      addPostIt({
        ...input,
        position: { x: centerX, y: centerY },
      });
    },
    [addPostIt, pan, zoom]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !thinkSpace) {
    return (
      <AppLayout title="Error">
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error?.message || 'ThinkSpace not found'}
          </Typography>
          <Typography color="text.secondary">
            This ThinkSpace may have been deleted or you don't have access.
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  if (!access.hasAccess) {
    return (
      <AppLayout title="Access Denied">
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            You don't have permission to view this ThinkSpace.
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  const sortedElements = [...thinkSpace.elements].sort((a, b) => a.zIndex - b.zIndex);

  return (
    <AppLayout title={thinkSpace.name} showBackToDashboard>
      {/* Read-only indicator */}
      {!access.canEdit && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          You have view-only access to this ThinkSpace
        </Alert>
      )}

      {/* Canvas */}
      <Box
        ref={canvasRef}
        data-canvas-container
        onClick={handleCanvasClick}
        onMouseDown={handleDrawStart}
        onMouseMove={handleDrawMove}
        onMouseUp={handleDrawEnd}
        onMouseLeave={handleDrawEnd}
        onTouchStart={handleDrawStart}
        onTouchMove={handleDrawMove}
        onTouchEnd={handleDrawEnd}
        sx={{
          position: 'relative',
          width: '100%',
          height: 'calc(100vh - 64px)',
          overflow: 'auto',
          backgroundColor: thinkSpace.backgroundColor,
          cursor: activeTool === 'line' || activeTool === 'box' || activeTool === 'ellipse' || activeTool === 'diamond' || activeTool === 'connector' ? 'crosshair' : 'default',
        }}
      >
        <Box
          ref={canvasContentRef}
          sx={{
            position: 'relative',
            width: thinkSpace.canvasSize.width * zoom,
            height: thinkSpace.canvasSize.height * zoom,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'top left',
          }}
        >
          {/* Render elements */}
          {sortedElements.map((element) => {
            const commonProps = {
              selected: selectedId === element.id,
              canEdit: access.canEdit,
              onSelect: () => setSelectedId(element.id),
              onUpdate: (updates: Partial<CanvasElement>) => updateElement(element.id, updates),
              onDelete: () => deleteElement(element.id),
              onBringToFront: () => bringToFront(element.id),
              onSendToBack: () => sendToBack(element.id),
              onBringForward: () => bringForward(element.id),
              onSendBackward: () => sendBackward(element.id),
              onDragStart: (e: React.MouseEvent | React.TouchEvent) => handleDragStart(element.id, e),
            };

            switch (element.type) {
              case 'postit':
                return <PostItNote key={element.id} element={element} {...commonProps} />;
              case 'text':
                return <TextElement key={element.id} element={element} {...commonProps} />;
              case 'line':
                return <LineElement key={element.id} element={element} {...commonProps} />;
              case 'box':
                return <BoxElement key={element.id} element={element} {...commonProps} />;
              case 'ellipse':
                return <EllipseElement key={element.id} element={element} {...commonProps} />;
              case 'diamond':
                return <DiamondElement key={element.id} element={element} {...commonProps} />;
              case 'connector':
                return (
                  <ConnectorElement
                    key={element.id}
                    element={element}
                    allElements={thinkSpace.elements}
                    selected={selectedId === element.id}
                    canEdit={access.canEdit}
                    onSelect={() => setSelectedId(element.id)}
                    onUpdate={(updates: Partial<CanvasElement>) => updateElement(element.id, updates)}
                    onDelete={() => deleteElement(element.id)}
                    onBringToFront={() => bringToFront(element.id)}
                    onSendToBack={() => sendToBack(element.id)}
                    onBringForward={() => bringForward(element.id)}
                    onSendBackward={() => sendBackward(element.id)}
                  />
                );
              case 'image':
                return <ImageElement key={element.id} element={element} {...commonProps} />;
              default:
                return null;
            }
          })}

          {/* Temp element while drawing */}
          {tempElement && tempElement.type === 'line' && (
            <LineElement
              element={tempElement as any}
              selected={false}
              canEdit={false}
              onSelect={() => {}}
              onUpdate={() => {}}
              onDelete={() => {}}
              onBringToFront={() => {}}
              onSendToBack={() => {}}
              onBringForward={() => {}}
              onSendBackward={() => {}}
              onDragStart={() => {}}
            />
          )}
          {tempElement && tempElement.type === 'box' && (
            <BoxElement
              element={tempElement as any}
              selected={false}
              canEdit={false}
              onSelect={() => {}}
              onUpdate={() => {}}
              onDelete={() => {}}
              onBringToFront={() => {}}
              onSendToBack={() => {}}
              onBringForward={() => {}}
              onSendBackward={() => {}}
              onDragStart={() => {}}
            />
          )}
          {tempElement && tempElement.type === 'ellipse' && (
            <EllipseElement
              element={tempElement as any}
              selected={false}
              canEdit={false}
              onSelect={() => {}}
              onUpdate={() => {}}
              onDelete={() => {}}
              onBringToFront={() => {}}
              onSendToBack={() => {}}
              onBringForward={() => {}}
              onSendBackward={() => {}}
              onDragStart={() => {}}
            />
          )}
          {tempElement && tempElement.type === 'diamond' && (
            <DiamondElement
              element={tempElement as any}
              selected={false}
              canEdit={false}
              onSelect={() => {}}
              onUpdate={() => {}}
              onDelete={() => {}}
              onBringToFront={() => {}}
              onSendToBack={() => {}}
              onBringForward={() => {}}
              onSendBackward={() => {}}
              onDragStart={() => {}}
            />
          )}
          {tempElement && tempElement.type === 'connector' && (
            <>
              <ConnectorElement
                element={tempElement as any}
                allElements={thinkSpace.elements}
                selected={false}
                canEdit={false}
                onSelect={() => {}}
                onUpdate={() => {}}
                onDelete={() => {}}
                onBringToFront={() => {}}
                onSendToBack={() => {}}
                onBringForward={() => {}}
                onSendBackward={() => {}}
              />
              {/* Snap point indicators */}
              {connectorStartSnap && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: connectorStartSnap.point.x - 8,
                    top: connectorStartSnap.point.y - 8,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    border: '2px solid white',
                    boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                    zIndex: 10000,
                    pointerEvents: 'none',
                  }}
                />
              )}
              {connectorEndSnap && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: connectorEndSnap.point.x - 8,
                    top: connectorEndSnap.point.y - 8,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    border: '2px solid white',
                    boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                    zIndex: 10000,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Toolbar */}
      <Toolbar
        activeTool={activeTool}
        onToolChange={setActiveTool}
        zoom={zoom}
        onZoomChange={setZoom}
        onExport={handleExport}
        onImport={handleImport}
        onShare={() => setShareOpen(true)}
        onImage={() => setImageLibraryOpen(true)}
        canEdit={access.canEdit}
        isOwner={access.isOwner}
      />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Share Dialog */}
      {thinkSpace && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          thinkSpace={thinkSpace}
        />
      )}

      {/* Create Post-it Wizard */}
      <CreatePostItWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreate={handleWizardCreate}
        isFirstNote={thinkSpace.elements.length === 0}
      />

      {/* Image Library Dialog */}
      {user && (
        <ImageLibraryDialog
          open={imageLibraryOpen}
          onClose={() => setImageLibraryOpen(false)}
          thinkSpaceId={id || ''}
          userId={user.uid}
          imageLibrary={thinkSpace.imageLibrary || []}
          canEdit={access.canEdit}
          onSelectImage={(image) => {
            // Scale down if too large
            let width = image.width || 200;
            let height = image.height || 200;
            const maxSize = 400;
            if (width > maxSize || height > maxSize) {
              const scale = maxSize / Math.max(width, height);
              width *= scale;
              height *= scale;
            }

            // Position image in center of visible canvas area
            let centerX = 100;
            let centerY = 100;
            const canvas = canvasRef.current;
            if (canvas) {
              const rect = canvas.getBoundingClientRect();
              // Simple centering: place at center of visible area
              centerX = Math.max(50, (rect.width - width) / 2);
              centerY = Math.max(50, (rect.height - height) / 2);
            }

            addImage({
              imageUrl: image.imageUrl,
              imagePath: image.imagePath,
              size: { width, height },
              position: { x: centerX, y: centerY },
            });
          }}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Element</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this element? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};
